import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { toMediaUrl } from '../lib/mediaUrl';
import type { Database } from '../types/database.types';

type Notification = Database['public']['Tables']['notifications']['Row'];

export type GenerationPreview = {
  title: string;
  message?: string;
  mediaUrl: string;
  mediaType?: string;
  notificationId?: string;
};

export type GenerationToastItem = GenerationPreview & {
  id: string;
  createdAt: number;
};

type GenerationToastContextValue = {
  toasts: GenerationToastItem[];
  preview: GenerationPreview | null;
  pushCompletionToast: (item: Omit<GenerationToastItem, 'id' | 'createdAt'> & { id?: string }) => void;
  dismissToast: (id: string) => void;
  openPreview: (preview: GenerationPreview) => void;
  closePreview: () => void;
};

const GenerationToastContext = createContext<GenerationToastContextValue | null>(null);

function normalizePreview(input: GenerationPreview): GenerationPreview {
  return {
    ...input,
    mediaUrl: toMediaUrl(input.mediaUrl),
    mediaType: input.mediaType || 'video',
  };
}

export function previewFromNotification(n: Notification): GenerationPreview | null {
  const meta =
    n.metadata && typeof n.metadata === 'object' && !Array.isArray(n.metadata)
      ? (n.metadata as Record<string, unknown>)
      : {};
  const resultUrl = typeof meta.result_url === 'string' ? meta.result_url : '';
  if (!resultUrl) return null;
  return {
    title: n.title || 'Vídeo pronto',
    message: n.message || undefined,
    mediaUrl: resultUrl,
    mediaType: typeof meta.media_type === 'string' ? meta.media_type : 'video',
    notificationId: n.id,
  };
}

export function GenerationToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<GenerationToastItem[]>([]);
  const [preview, setPreview] = useState<GenerationPreview | null>(null);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushCompletionToast = useCallback(
    (item: Omit<GenerationToastItem, 'id' | 'createdAt'> & { id?: string }) => {
      const normalized = normalizePreview(item);
      const id = item.id || `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => {
        if (
          prev.some(
            (t) =>
              t.id === id ||
              (t.notificationId && t.notificationId === item.notificationId) ||
              (item.mediaUrl && t.mediaUrl === normalized.mediaUrl),
          )
        ) {
          return prev;
        }
        return [...prev, { ...normalized, id, createdAt: Date.now() }].slice(-4);
      });
    },
    [],
  );

  const openPreview = useCallback((next: GenerationPreview) => {
    setPreview(normalizePreview(next));
  }, []);

  const closePreview = useCallback(() => setPreview(null), []);

  const value = useMemo(
    () => ({
      toasts,
      preview,
      pushCompletionToast,
      dismissToast,
      openPreview,
      closePreview,
    }),
    [toasts, preview, pushCompletionToast, dismissToast, openPreview, closePreview],
  );

  return (
    <GenerationToastContext.Provider value={value}>{children}</GenerationToastContext.Provider>
  );
}

export function useGenerationToast() {
  const ctx = useContext(GenerationToastContext);
  if (!ctx) {
    throw new Error('useGenerationToast must be used within GenerationToastProvider');
  }
  return ctx;
}

export function useGenerationToastOptional() {
  return useContext(GenerationToastContext);
}
